import React, { useState } from 'react';
import { Star, Send, CheckCircle2, Heart } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TestimonialFormStandalone() {
  const [formData, setFormData] = useState({
    name: '',
    role: 'Parent',
    review: '',
    rating: 5
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.review.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.review.length < 20) {
      alert('Please write at least 20 characters in your review');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/testimonials`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full text-center">
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-12">
            <div className="bg-green-100 rounded-full p-6 inline-block mb-6">
              <CheckCircle2 className="w-16 h-16 text-green-600" strokeWidth={1.5} />
            </div>
            
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Thank You! 💙
            </h1>
            
            <p className="text-lg text-slate-600 mb-8">
              Your feedback has been submitted successfully. We truly appreciate you taking the time to share your thoughts!
            </p>

            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
              <p className="text-sm text-slate-600">
                Your testimonial will be reviewed by our team. Thank you for your support!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
      <div className="max-w-3xl w-full">
        {/* Simple Header */}
        <div className="text-center mb-8">
          <div className="bg-blue-100 rounded-xl p-4 inline-block mb-6">
            <Heart className="w-12 h-12 text-blue-600" strokeWidth={1.5} />
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Share Your Experience
          </h1>
          
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            We'd love to hear about your experience. Your honest feedback helps us improve and helps other parents make informed decisions.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-8 sm:p-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Sarah Johnson"
                required
                className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                You are a <span className="text-red-500">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
              >
                <option value="Parent">Parent</option>
                <option value="Teacher">Teacher</option>
                <option value="Student">Student</option>
                <option value="School Administrator">School Administrator</option>
              </select>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRatingClick(star)}
                    className="transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        star <= formData.rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-slate-300'
                      }`}
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
                <span className="ml-3 text-lg font-semibold text-slate-700">
                  {formData.rating} / 5
                </span>
              </div>
            </div>

            {/* Review */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Your Review <span className="text-red-500">*</span>
              </label>
              <textarea
                name="review"
                value={formData.review}
                onChange={handleChange}
                placeholder="How has Mrs. Zeenat helped you? How was the experience? What did you appreciate the most?"
                required
                rows={6}
                className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all resize-none placeholder:text-slate-400"
              />
              <p className="text-sm text-slate-500 mt-2">
                Minimum 20 characters • {formData.review.length} characters
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white rounded-xl px-8 py-4 font-semibold shadow-md hover:bg-blue-700 hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" strokeWidth={2} />
                  Submit Feedback
                </>
              )}
            </button>
          </form>
        </div>

        {/* Privacy Note */}
        <p className="text-sm text-slate-500 text-center mt-6">
          Your feedback is valuable to us. We respect your privacy and will never share your personal information.
        </p>
      </div>
    </div>
  );
}

export default TestimonialFormStandalone;
